import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { ApiError } from "./api";
import type { Async } from "./useAsync";

/** The em-dash a row shows instead of a number. Absent is not zero, and must not look it. */
export const DASH = "—";
const NBSP = " ";

// ── formatters ─────────────────────────────────────────────────────────────────

export function when(iso?: string | null): string {
  if (!iso) return DASH;
  const d = new Date(iso);
  if (isNaN(+d)) return String(iso);
  return d.toLocaleString(undefined,
    { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Relative time, coarsening as it grows. "3d ago" is more useful than a date at a glance. */
export function ago(seconds?: number | null): string {
  if (seconds == null) return DASH;
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)}m ago`;
  if (s < 172800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

/** ⚠️ A NON-BREAKING SPACE and zero-padded seconds, so `4m 03s` never wraps or ragged-edges. */
export function dur(seconds?: number | null): string {
  if (seconds == null) return DASH;
  const s = Math.round(seconds);
  return s < 60 ? `${s}s`
                : `${Math.floor(s / 60)}m${NBSP}${String(s % 60).padStart(2, "0")}s`;
}

/** ⚠️ FOUR DECIMALS. A grading run costs cents; `$0.06` rounded to `$0.06` is fine but
 *  `$0.0004` rounded to `$0.00` reads as free, and free is a different claim. */
export function usd(n?: number | null): string {
  return n == null ? DASH : `$${n.toFixed(4)}`;
}

/** Nullish LAST in BOTH directions — otherwise a descending sort fills the top of the table
 *  with rows that have no value at all. A missing mark is not a low mark. */
export function sorted<T>(rows: T[], key: (r: T) => number | string | null | undefined,
                          dir: 1 | -1 = 1): T[] {
  return [...rows].sort((a, b) => {
    const x = key(a), y = key(b);
    if (x == null && y == null) return 0;
    if (x == null) return 1;
    if (y == null) return -1;
    return x < y ? -dir : x > y ? dir : 0;
  });
}

// ── theme ──────────────────────────────────────────────────────────────────────

export type Theme = "dark" | "light";

/**
 * 🔴 DARK IS THE DEFAULT, NOT THE OPERATING SYSTEM'S CHOICE. This used to be three states —
 * `auto`, `light`, `dark` — with `auto` deleting the attribute and letting
 * `color-scheme: light dark` follow the OS. That is a defensible default for a generic tool
 * and the wrong one here: the product has a dark identity, the brand band is dark in both
 * themes because the wordmark has no dark-on-light variant, and a viewer whose laptop happened
 * to be in light mode met a light console with a dark strip across the top of it.
 *
 * ⚠️ TWO STATES, SO ONE CONTROL CAN SHOW BOTH. A three-way cycle behind a single icon cannot
 * say which of the three it is on; a switch can. Light is still one click away — the point is
 * which one you get without asking.
 *
 * ⚠️ `localStorage` IS WRAPPED. A private window or blocked site data makes the accessor throw,
 * and a theme preference is not worth a blank page.
 */
export const THEME_KEY = "tc-theme";

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return localStorage.getItem(THEME_KEY) === "light" ? "light" : "dark";
    } catch { return "dark"; }
  });
  useEffect(() => {
    // Always stamped, never deleted: `:root` is dark and `[data-theme="light"]` is what
    // overrides it, so an absent attribute now means dark rather than "ask the OS".
    document.documentElement.dataset.theme = theme;
    try { localStorage.setItem(THEME_KEY, theme); } catch { /* no storage: dark, every time */ }
  }, [theme]);
  return [theme, setTheme];
}

/**
 * One small button. It shows the theme you would GET, and its label says so — an icon alone is
 * ambiguous in either direction, and this product has no icon set, so the glyph is text.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  const next: Theme = theme === "dark" ? "light" : "dark";
  return (
    <button className="themebtn" type="button"
            title={`Switch to the ${next} theme`}
            aria-label={`Switch to the ${next} theme`}
            onClick={() => setTheme(next)}>
      <span aria-hidden="true">{next === "light" ? "☀" : "☾"}</span>
    </button>
  );
}

// ── shell primitives ───────────────────────────────────────────────────────────

export const Card = ({ children, className = "" }:
  { children: ReactNode; className?: string }) =>
  <div className={`card ${className}`}>{children}</div>;

/** ⚠️ `desc` IS NOT DECORATION. It is where a panel says what it is claiming and what it is
 *  not — the epistemics that would otherwise be a paragraph nobody reads above the table. */
export const CardHead = ({ title, desc, right }:
  { title: ReactNode; desc?: ReactNode; right?: ReactNode }) => (
  <div className="card-head">
    <div style={{ minWidth: 0 }}>
      <div className="card-title">{title}</div>
      {desc && <div className="card-desc">{desc}</div>}
    </div>
    {right && <><div className="spacer" /><div className="row">{right}</div></>}
  </div>
);

/** `tight` removes the padding so a table can sit flush to the card edge under its header. */
export const CardBody = ({ children, tight = false }:
  { children: ReactNode; tight?: boolean }) =>
  <div className={`card-body${tight ? " tight" : ""}`}>{children}</div>;

// ── status ─────────────────────────────────────────────────────────────────────

export type Tone = "ok" | "warn" | "stop" | "flag" | "held" | "plain";

/**
 * 🔴 SIX TONES, AND THE TWO THE CONSOLE LACKED ARE THE POINT.
 * `held` is hollow and colourless — an infrastructure hold is the ABSENCE of a result, and
 * painting it red (which this console did) says the student failed. `flag` is violet, off
 * the ok/warn/stop axis, because originality never changes a score.
 */
export const STATUS_TONE: Record<string, Tone> = {
  READY: "ok", RELEASED: "ok", OK: "ok", recorded: "ok",
  REVIEW: "warn", WARN: "warn", refused: "warn",
  HELD: "held", UNKNOWN: "held", running: "held", held: "held",
  STOP: "stop", STALE: "stop", NEVER: "stop", failed: "stop", abandoned: "stop",
};

export const Badge = ({ tone = "plain", title, dot = false, children }: {
  tone?: Tone; title?: string; dot?: boolean; children: ReactNode;
}) => (
  <span className={`badge ${tone}`} title={title}>
    {dot && <span className="dot" />}{children}
  </span>
);

/**
 * Which tier a setting's value came from, and the one control that changes that.
 *
 * 🔴 THE CONSOLE HAS NEVER BEEN ABLE TO SAY THIS. `teacher_settings.read()` merges the global
 * row and the cohort's own into one flat dict, so a teacher changing a global default could
 * not know which cohorts would follow it and a teacher reading a cohort's settings could not
 * tell what they had diverged from. The schema has always allowed the second tier; nothing
 * could write it.
 *
 * 🔴 `Reset to global` DELETES THE COHORT'S ROW; IT DOES NOT COPY THE GLOBAL VALUE IN. The two
 * look identical the moment they happen and diverge for ever after — a deleted row follows the
 * global, a copied value silently stops following it — so the button says which it is.
 *
 * ⚠️ CYAN MEANS EXACTLY ONE THING: set at this level, departing from the level above. It is
 * not a kind, not a status and not an emphasis; `05-answers-and-teams.md` Part C is the rule
 * and this is its first consumer.
 *
 * ⚠️ THE WORD CARRIES THE STATE, NOT THE COLOUR. These get pasted into chat and read aloud.
 */
export const Tier = ({ tier, editable, onOverride, onReset }: {
  tier?: { source: string; inherited: unknown };
  editable: boolean;
  onOverride: () => void;
  onReset: () => void;
}) => {
  if (!tier) return null;
  const own = tier.source === "course";
  return (
    <span className="tier">
      <span className={own ? "tierchip own" : "tierchip"}>
        {own ? "overridden" : "inherited"}
      </span>
      {own && (
        <span className="tier-was" title="The global default this cohort departs from">
          global: <s>{String(tier.inherited)}</s>
        </span>)}
      {editable && (own
        ? <button className="btn ghost sm" onClick={onReset}
                  title="Delete this cohort's own value so it follows the global default again
— including whenever the global changes later.">Reset to global</button>
        : <button className="btn ghost sm" onClick={onOverride}
                  title="Pin this cohort to its own value, so a later change to the global
default does not move it.">Override here</button>)}
    </span>
  );
};

/**
 * Where an effective value was actually decided, across every tier that can decide it.
 *
 * 🔴 `auto_grade` IS DECIDED IN THREE PLACES AND THE PAGE SHOWED ONE. The global row, the
 * cohort's own, and the assignment's switch — and the third is an **and**, not an override:
 * `poller.unattended_block` refuses unless the binding is explicitly true, so a cohort
 * switched on with the assignment unset grades nothing. Only the assignment row knew that.
 *
 * ⚠️ `line-through` IS NOT ANNOUNCED, so a superseded segment carries its state in its own
 * accessible name. Every segment prints its tier and its value as text; the colour is
 * redundant on purpose, which is the same rule the status words follow.
 *
 * ⚠️ CYAN IS THE WINNING SEGMENT AND NOTHING ELSE ON THE STRIP. It means "set at this level,
 * departing from the level above" — `05-answers-and-teams.md` Part C — and a trace is the
 * clearest place that could go wrong, because three segments invite three colours.
 */
export const Trace = ({ segments }: {
  segments: { tier: string; value: string; state: "wins" | "superseded" | "unset" }[];
}) => (
  <span className="trace">
    {segments.map((s) => (
      <span key={s.tier} className={`trace-seg ${s.state}`}
            aria-label={`${s.tier}, ${s.value}${s.state === "superseded" ? ", superseded"
                                                : s.state === "unset" ? ", not set" : ""}`}>
        {s.tier} · {s.value}
      </span>))}
  </span>
);

/**
 * The cohort filter on a page that is not about one cohort.
 *
 * 🔴 A CONTAINER CANNOT BE REMOVED; A FILTER CAN, AND THE AFFORDANCE SAYS WHICH THIS IS. Runs
 * and Logs read global tables that happen to carry a `course_id` — `runs.attempt_rows` has
 * always taken it as a plain `WHERE`. It was only ever presented as a container, which is why
 * the sidebar's picker looked like it governed them.
 *
 * ⚠️ IT SAYS WHERE THE FILTER CAME FROM. A filter somebody chose and a filter they inherited
 * by navigating are different things, and only the second needs explaining.
 */
export const ScopeBar = ({ name, on, count, onChange }: {
  name: string; on: boolean; count: number; onChange: (on: boolean) => void;
}) => (
  <div className="scopebar">
    <span className="kick">Filter</span>
    {on ? (
      <span className="cohort-chip">
        {name}
        <button type="button" aria-label={`Remove the ${name} filter and see every cohort`}
                title="Remove this filter and see every cohort"
                onClick={() => onChange(false)}>×</button>
      </span>
    ) : (
      <button className="chip" onClick={() => onChange(true)}>
        Narrow to {name}
      </button>
    )}
    <span className="spacer" />
    <span className="small muted">
      {on
        ? `Showing ${name}. This page holds every cohort you teach.`
        : `Every cohort you teach — ${count} row(s).`}
    </span>
  </div>
);

/**
 * What a cohort filter is hiding, said out loud.
 *
 * 🔴 WHEN YOU ARE ONE FILTER AWAY FROM A SECOND IDENTICAL FAILURE, hiding it entirely is how
 * the same bug gets diagnosed twice. The near-miss rows render dimmed above this line rather
 * than being dropped.
 */
export const NearMiss = ({ n, onInclude }: { n: number; onInclude: () => void }) =>
  n === 0 ? null : (
    <p className="nearmiss">
      <strong>{n}</strong> more in another cohort match{n === 1 ? "es" : ""} everything except
      the cohort filter — shown dimmed above.{" "}
      <button className="linkish" onClick={onInclude}>Include them →</button>
    </p>
  );

/** The uppercase word IS the status; the colour is redundant on purpose. */
export const State = ({ s }: { s?: string | null }) => {
  if (!s) return <span className="muted">{DASH}</span>;
  return <Badge tone={STATUS_TONE[s] ?? "plain"} dot>{s}</Badge>;
};

export const Money = ({ usd: n }: { usd?: number | null }) =>
  n == null ? <span className="muted">{DASH}</span> : <span className="num">{usd(n)}</span>;

export const When = ({ iso }: { iso?: string | null }) =>
  !iso ? <span className="muted">{DASH}</span> : <span title={iso}>{when(iso)}</span>;

// ── the four states, decided once ──────────────────────────────────────────────

/**
 * 🔴 A REFUSAL AND A FAULT ARE DIFFERENT ANSWERS AND MUST NOT LOOK ALIKE. A refusal is a
 * decision: something chose this and the sentence names the remedy. A fault is something
 * broken. The `Refused:` / `Infrastructure:` prefix convention carries that distinction from
 * the jail's shell script all the way to this component.
 */
export function ErrorBox({ err, onRetry }: { err: ApiError; onRetry?: () => void }) {
  const refused = err.kind === "refused";
  return (
    <div className={`alert ${refused ? "refused" : ""}`} role="alert">
      <span className="ico">{refused ? "⛔" : "⚠️"}</span>
      <div className="body">
        <div className="t">{refused ? "Refused" : "Could not load this"}</div>
        <div className="m">{err.message}</div>
        {(err.consentUrl || (onRetry && !refused)) && (
          <div className="row" style={{ marginTop: 8 }}>
            {err.consentUrl && <a className="btn sm" href={err.consentUrl}>Grant access</a>}
            {onRetry && !refused &&
              <button className="btn sm" onClick={onRetry}>Try again</button>}
          </div>)}
      </div>
    </div>
  );
}

/** ⚠️ ALWAYS TWO LINES: the fact, then what would put a row here. "No runs yet" alone leaves
 *  a teacher unable to tell an empty queue from a broken one. */
export const Empty = ({ title, hint }: { title: string; hint?: ReactNode }) => (
  <div className="empty">
    <div className="big">{title}</div>
    {hint && <div>{hint}</div>}
  </div>
);

/** Staggered widths so it reads as text rather than as bars, and preserves the layout so the
 *  page does not jump when the data lands. */
export const Skeleton = ({ rows = 4 }: { rows?: number }) => (
  <div className="card-body">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="skel" style={{ marginBottom: 10, width: `${92 - i * 11}%` }} />))}
  </div>
);

/**
 * 🔴 LOADING / ERROR / EMPTY / CONTENT, DECIDED IN ONE PLACE. The console re-made this
 * decision by hand in SEVEN tabs, each rendering a bare `<p>Loading…</p>`, so they could and
 * did drift. One component means every panel agrees, and a skeleton replaces a word.
 */
export function Pane<T>({ q, empty, children }: {
  q: Async<T>; empty?: ReactNode; children: (data: T) => ReactNode;
}) {
  if (q.error) return <div className="card-body"><ErrorBox err={q.error} onRetry={q.reload} /></div>;
  if (q.data == null) return <Skeleton />;
  if (empty && Array.isArray(q.data) && q.data.length === 0) return <>{empty}</>;
  return <>{children(q.data)}</>;
}

/** "as of 14:32 · Refresh". 🔴 A CACHED PANEL THAT DOES NOT SAY HOW OLD IT IS is the
 *  dishonest half of caching: the reader takes it for live. */
export const Freshness = ({ q }: { q: Async<unknown> }) => (
  <span className="row small muted" style={{ gap: 8 }}>
    {q.loading ? "loading…"
      : q.fetchedAt ? `as of ${new Date(q.fetchedAt).toLocaleTimeString()}` : ""}
    <button className="btn sm ghost" onClick={q.reload} disabled={q.loading}>
      {q.loading ? "Refreshing…" : "Refresh"}</button>
  </span>
);

// ── table sorting ──────────────────────────────────────────────────────────────

export type Sort = { key: string; dir: 1 | -1 };

/** A column that can be sorted always LOOKS sortable — a dim ↕ until active, then ▲/▼. */
export const Th = ({ label, k, sort, setSort, right = false, title }: {
  label: ReactNode; k: string; sort: Sort; setSort: (s: Sort) => void;
  right?: boolean; title?: string;
}) => {
  const on = sort.key === k;
  return (
    <th className={`sortable${right ? " r" : ""}`} title={title}
        aria-sort={on ? (sort.dir === 1 ? "ascending" : "descending") : undefined}
        onClick={() => setSort({ key: k, dir: on && sort.dir === 1 ? -1 : 1 })}>
      {label}<span className="arrow">{on ? (sort.dir === 1 ? "▲" : "▼") : "↕"}</span>
    </th>
  );
};

// ── KPI ────────────────────────────────────────────────────────────────────────

/**
 * ⚠️ EVERY KPI CARRIES A `hint` SENTENCE. A number under a one-word label tells a teacher
 * nothing about what it counts — "held: 3" is not actionable until something says a held run
 * has no score at all. And the value is `DASH` while loading, never 0: a zero that turns out
 * to be a loading state is a lie the reader has already acted on.
 */
export const Kpi = ({ label, value, hint, tone }: {
  label: string; value: ReactNode; hint?: ReactNode; tone?: Tone;
}) => (
  <Card>
    <div className="kpi">
      <div className="label">
        {tone && <span className="swatch" style={{ background: `var(--tb-${tone})` }} />}
        {label}
      </div>
      <div className="value">{value ?? DASH}</div>
      {hint && <div className="hint">{hint}</div>}
    </div>
  </Card>
);

// ── inputs ─────────────────────────────────────────────────────────────────────

/**
 * 🔴 A CONTROLLED NUMBER INPUT CANNOT BE TYPED INTO. `value={String(n)}` with
 * `onChange={Number(...)}` round-trips every keystroke through Number: typing `0`, `.`, `7`
 * gives "0" → Number("0.") === 0 → React rewrites the DOM value back to "0", swallowing the
 * dot → then "07" → 7. So a confidence floor of 0.7 becomes 7, and a daily cap of 2.5
 * becomes 25. The draft string is what the user is typing; the number is committed only
 * when it parses.
 */
export function NumInput({ value, onCommit, mode = "decimal", disabled, min, max, step }: {
  value: number; onCommit: (n: number) => void;
  mode?: "numeric" | "decimal"; disabled?: boolean;
  min?: number; max?: number; step?: number;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <input className="input num" inputMode={mode} disabled={disabled}
           min={min} max={max} step={step}
           value={text ?? String(value)}
           onChange={(e) => {
             const t = e.target.value;
             setText(t);
             if (t.trim() !== "" && Number.isFinite(Number(t))) onCommit(Number(t));
           }}
           onBlur={() => setText(null)} />
  );
}

/** Label and its explanation on the left, control on its own track — a real settings form.
 *  A label and a control separated by a spacer puts every control at the right window edge,
 *  far from the words that name it. */
/**
 * One setting: what it is, what it does, and — one click away — why it is safe.
 *
 * 🔴 THE CONTROL IS THE POINT; THE RATIONALE IS NOT. This used to render the whole `note`
 * inline in a 190px column at 12px — a hundred words of prose beside a 60px dropdown. The
 * page read as a design document with controls hidden in it, which is the opposite of what a
 * settings screen is for: it is SCANNED AND OPERATED, not read top to bottom.
 *
 * ⚠️ THE NOTES ARE NOT DELETED, AND THAT MATTERS. Each one states the mechanism behind a
 * promise — this project's whole rule is that a setting named in prose must be enforced by
 * something — so a teacher must still be able to reach it. `hint` is the one line you need to
 * operate the control; `note` is the reasoning, now behind the info button beside the label.
 *
 * `wide` puts the control on its own row, for anything a single line cannot hold.
 */
export const Field = ({ label, hint, note, wide = false, children }: {
  label: ReactNode; hint?: ReactNode; note?: ReactNode; wide?: boolean; children: ReactNode;
}) => (
  <div className="field">
    <div className={wide ? "field-head stack-tight" : "field-head"}>
      <div className="field-main">
        <div className="field-label">{label}{note && <Why>{note}</Why>}</div>
        {hint && <div className="field-hint">{hint}</div>}
      </div>
      <div className={wide ? "field-ctl wide" : "field-ctl"}>{children}</div>
    </div>
  </div>
);

/**
 * The reasoning behind a control, one keystroke or one tap away.
 *
 * 🔴 NOT A `title=` TOOLTIP, AND THE DIFFERENCE IS THE WHOLE COMPONENT. This project has twice
 * recorded what a native tooltip costs: it is invisible on a screenshot (and these screens get
 * pasted into chat), invisible on touch, and invisible to a screen reader — which is why the
 * Runs page renders a refusal's sentence as text and why an empty build-log cell is a button
 * rather than a dash with a hover. These notes also run to 133 words, and a hover strip cannot
 * hold a paragraph.
 *
 * So it opens three ways, and the third is the one a native tooltip cannot do:
 *   hover   — a mouse user gets it for free, and it goes when the mouse does
 *   focus   — Tab reaches it, because it is a real <button> in the tab order
 *   click   — a tap opens and holds it, which is the only route on a touch screen; a click
 *             holds what a hover opened, and a second one puts it away
 * `aria-describedby` ties it to the label, so a screen reader announces the reasoning as part
 * of the control rather than as a stray paragraph. Escape closes it, and so does focus leaving.
 * 🔴 [X] A TAP OPENED NOTHING UNTIL 2026-10-03, ON EVERY PAGE THAT HAS ONE (measured in Chromium
 * with touch). A touch browser follows a tap with the mouse events it emulates — mouseenter, a
 * focus, then the click — and the first two each opened the panel, so the click found it open
 * and closed it. The click now asks WHO opened it: only a panel a click opened is put away by
 * one. And leaving puts away only what a hover opened, so a click holds it.
 *
 * ⚠️ POSITIONED `fixed`, FROM THE TRIGGER'S OWN RECTANGLE. `.card` sets `overflow: hidden` to
 * clip its rounded corners, so an absolutely-positioned panel would be cut off at the card
 * edge — worst for the last field in a card, which is exactly where nobody would look for a
 * bug. Fixed positioning cannot be clipped by an ancestor; it closes on scroll instead, since
 * a panel anchored to a rectangle that has moved is pointing at the wrong control.
 * ⚠️ AND MOVED BACK INSIDE THE WINDOW ONCE IT HAS A WIDTH (2026-10-03). It opens from the
 * button's left edge, so a button at the right of a narrow screen — the grade report's
 * ESCALATED badge, with its rail on top below 1000px — opened a panel mostly past the edge, on
 * the touch screen this exists for. It keeps the 12px gutter it already keeps on the left.
 *
 * `label` names what it explains; a setting's says why it is safe, as it always has.
 */
export function Why({ children, label = "Why this setting is safe" }: {
  children: ReactNode; label?: string;
}) {
  // Who opened it, because that decides what puts it away: a hover goes with the mouse, a
  // click stays for the next click, and focus leaving puts away all three.
  const [how, setHow] = useState<"hover" | "focus" | "click" | null>(null);
  const open = how !== null;
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLSpanElement>(null);
  const id = useId();

  useLayoutEffect(() => {
    const p = open && at ? pop.current?.getBoundingClientRect() : undefined;
    if (!p || !at) return;
    const room = window.innerWidth - 12;
    if (p.right <= room) return;
    const left = Math.max(12, room - p.width);
    if (left !== at.left) setAt({ top: at.top, left });
  }, [open, at]);

  const show = (by: "hover" | "focus" | "click") => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setAt({ top: r.bottom + 6, left: Math.max(12, r.left - 8) });
    setHow(by);
  };

  useEffect(() => {
    if (!open) return;
    const close = () => setHow(null);
    // A panel anchored to a rectangle that has scrolled away points at the wrong control.
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  return (
    <span className="why">
      <button ref={ref} type="button" className="why-btn"
              aria-label={label} aria-expanded={open}
              aria-describedby={open ? id : undefined}
              onMouseEnter={() => { if (!open) show("hover"); }}
              onMouseLeave={() => { if (how === "hover") setHow(null); }}
              onFocus={() => show("focus")} onBlur={() => setHow(null)}
              onClick={(e) => {
                e.preventDefault();
                if (how === "click") setHow(null); else show("click");
              }}
              onKeyDown={(e) => {
                if (e.key !== "Escape") return;
                // ⚠️ AN ESCAPE THAT CLOSES AN OPEN Why IS ITS OWN: a sheet around it reads
                // `defaultPrevented` and stays open (2026-10-06).
                if (open) e.preventDefault();
                setHow(null); ref.current?.blur();
              }}>
        i
      </button>
      {open && at && (
        <span ref={pop} id={id} role="tooltip" className="why-pop"
              style={{ top: at.top, left: at.left }}>{children}</span>)}
    </span>
  );
}

// ── markdown ───────────────────────────────────────────────────────────────────

/**
 * 🔴 REACT NODES, NEVER HTML, AND NO MARKDOWN LIBRARY. Guidance is the one string a teacher
 * writes that a grading model later reads, so there is deliberately no injection surface on
 * either side. Anything not covered renders as the literal text the author typed, which is
 * the honest failure.
 */
/** Two overlapping sheets: the copy icon, drawn in the text's own colour. */
export const CopyGlyph = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="none"
       stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="8" y="8" width="12" height="13" rx="2" />
    <path d="M4 16V5a2 2 0 0 1 2-2h9" />
  </svg>);

export function Markdown({ text, links = true }: { text: string; links?: boolean }) {
  if (!text.trim()) return null;
  return (
    <div className="md">
      {mdBlocks(text).map((b, i) => {
        const k = String(i);
        if (b.kind === "pre") {
          return <pre className="md-pre" key={k}><code>{b.lines.join("\n")}</code></pre>;
        }
        if (b.kind === "ul" || b.kind === "ol") {
          const items = b.lines.map((l, j) => <li key={j}>{inline(l, `${k}-${j}`, links)}</li>);
          return b.kind === "ul"
            ? <ul className="md-ul" key={k}>{items}</ul>
            : <ol className="md-ul" key={k}>{items}</ol>;
        }
        if (b.kind === "h1" || b.kind === "h2") {
          return <div key={k} className={b.kind === "h1" ? "md-h" : "md-h2"}>
            {inline(b.lines[0] ?? "", k, links)}</div>;
        }
        return <p key={k}>{inline(b.lines.join("\n"), k, links)}</p>;
      })}
    </div>
  );
}

/** One line of Markdown and no blocks: a headline that may carry `code` or **bold**, rendered
 *  inline where it stands. The same inline rules as `Markdown`.
 *  🔴 BUT NO LINKS UNLESS A CALLER ASKS FOR THEM — the opposite default to `Markdown`. It was
 *  made for a deduction's headline, which the grader wrote after reading a repository the
 *  student controls, so a caller that forgets the prop must get text, not an address. */
export function MarkdownInline({ text, links = false }: { text: string; links?: boolean }) {
  return <>{inline(text, "i", links)}</>;
}

type MdBlock = { kind: "p" | "ul" | "ol" | "h1" | "h2" | "pre"; lines: string[] };

/**
 * Lines into blocks: headings, bullet and numbered lists, fenced code and paragraphs.
 *
 * 🔴 LINE BY LINE, BECAUSE THAT IS HOW A MODEL WRITES MARKDOWN (2026-09-29). The first version
 * split on blank lines and asked each chunk whether it was ALL bullets or ONE heading — so the
 * commonest shape a grader writes, a `###` heading with its list directly under it, was neither,
 * and rendered as one paragraph with the hashes and dashes in it. A heading is a heading by
 * starting a line; a list item likewise.
 * ⚠️ An indented line straight after a list item continues it. An unclosed fence runs to the end,
 * which shows the text as the author typed it — the honest failure this component prefers.
 */
export function mdBlocks(text: string): MdBlock[] {
  const out: MdBlock[] = [];
  let cur: MdBlock | null = null;
  let fenced = false;
  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (fenced) {
      if (/^\s*```/.test(line)) { fenced = false; cur = null; } else cur!.lines.push(line);
      continue;
    }
    if (/^\s*```/.test(line)) {
      cur = { kind: "pre", lines: [] }; out.push(cur); fenced = true;
      continue;
    }
    if (!line.trim()) { cur = null; continue; }
    const h = /^\s*(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      out.push({ kind: h[1]!.length === 1 ? "h1" : "h2", lines: [h[2] ?? ""] });
      cur = null;
      continue;
    }
    const item = /^\s*(?:([-*+])|\d{1,3}[.)])\s+(.*)$/.exec(line);
    if (item) {
      const kind = item[1] ? "ul" : "ol";
      if (cur?.kind !== kind) { cur = { kind, lines: [] }; out.push(cur); }
      cur.lines.push(item[2] ?? "");
      continue;
    }
    if ((cur?.kind === "ul" || cur?.kind === "ol") && /^\s+\S/.test(line)) {
      cur.lines[cur.lines.length - 1] += ` ${line.trim()}`;
      continue;
    }
    if (cur?.kind !== "p") { cur = { kind: "p", lines: [] }; out.push(cur); }
    cur.lines.push(line);
  }
  return out;
}

function inline(s: string, keyBase: string, links = true): ReactNode[] {
  const out: ReactNode[] = [];
  // One pass, longest delimiter first, so `**a**` is never read as two italics.
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0, m: RegExpExecArray | null;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) out.push(s.slice(last, m.index));
    const t = m[0];
    const k = `${keyBase}-${m.index}`;
    if (t.startsWith("`")) out.push(<code key={k}>{t.slice(1, -1)}</code>);
    else if (t.startsWith("**")) out.push(<strong key={k}>{t.slice(2, -2)}</strong>);
    else if (t.startsWith("*")) out.push(<em key={k}>{t.slice(1, -1)}</em>);
    else {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(t)!;
      const [, label, href] = link;
      // ⚠️ http(s) ONLY. A `javascript:` or `data:` href in instructor text is not a link,
      // and rendering it as one would make a text box into an execution surface.
      // 🔴 AND NO LINK AT ALL IN TEXT A GRADER WROTE (`links={false}`). It wrote after reading a
      // repository the student controls, so a README can ask for a link on the teacher's own
      // report; the label is kept, the address is not.
      out.push(links && /^https?:\/\//.test(href ?? "")
        ? <a key={k} href={href} target="_blank" rel="noreferrer">{label}</a>
        : <span key={k}>{label}</span>);
    }
    last = m.index + t.length;
  }
  if (last < s.length) out.push(s.slice(last));
  return out;
}
